import type { Role } from './roles.js';
import { SCOPED_ROLES } from './roles.js';
import type { AuthenticatedUser } from './schemas/user.js';

export const ACTIONS = [
  'system.admin',
  'project.configure',
  'plan.manage',
  'catalog.edit',
  'run.execute',
  'defect.edit',
  'report.read',
  'signoff.confirm',
  'user.manage',
  // Task 7 (#113, A5): Notausgang fuer haengende Ketten — ein gesperrtes Kettenglied trotz
  // unfertigem Vorgaenger freigeben, mit Pflichtbegruendung. Bewusst ein EIGENES Recht statt
  // `plan.manage` mitzunutzen: `plan.manage` traegt auch der `verantwortlicher`, und A5 spricht
  // die Freigabe nur dem Testmanager zu. Vor allem aber darf sie NICHT an `run.execute` haengen —
  // das haette jeder Tester, und „der Tester selbst kann das nicht" (A5) waere damit hinfaellig:
  // wer seine eigene Sperre aufheben kann, unterliegt keiner.
  'e2e.release',
  // #111: „Fehlende Übersetzungen ergänzen" als Massenaktion über viele Testfälle. Eigenes Recht statt
  // `catalog.edit`: das tragen auch Verantwortlicher und Consultant, die Masse (hunderte KI-Aufrufe,
  // Kosten) soll aber nur Admin und Testmanager auslösen. Das Ergänzen EINES Testfalls bleibt
  // `catalog.edit`.
  'catalog.translate_bulk',
  // #195 (W1): Tests im Fachbereich weitergeben bzw. übernehmen. Eigenes Recht statt `run.execute`
  // mitzunutzen: `run.execute` tragen heute dieselben Rollen, aber die Weitergabe verschiebt die
  // Zuständigkeit (`assigned_tester_id`) und gehört damit nicht automatisch zu jeder künftigen
  // Rolle, die ausführen darf (z. B. ein reiner Prüfer). consultant und betrachter führen nichts
  // aus und geben deshalb auch nichts weiter.
  'run.handover',
  // #194: „Behebung bestätigen" — der Melder eines nicht blockierenden Fehlers bestätigt (oder
  // verneint), dass die Korrektur wirkt. Eigenes Recht statt `defect.edit` mitzunutzen: `defect.edit`
  // trägt der consultant, der den Fehler bearbeitet, aber nichts testet — er wäre Prüfer in eigener
  // Sache. Bestätigen darf, wer testet bzw. den Test verantwortet; consultant und betrachter melden
  // keine Fehler aus Läufen und prüfen deshalb auch keine Behebung.
  'defect.confirm_fix',
] as const;
export type Action = (typeof ACTIONS)[number];

// Basisrechte je Rolle (ohne Scope-Verfeinerung).
const MATRIX: Record<Role, ReadonlySet<Action>> = {
  admin: new Set(ACTIONS),
  testmanager: new Set<Action>([
    'project.configure',
    'plan.manage',
    'catalog.edit',
    'run.execute',
    'defect.edit',
    'report.read',
    'signoff.confirm',
    'user.manage',
    // A5: der Notausgang liegt beim Testmanager. `verantwortlicher` steht bewusst NICHT dabei —
    // der Plan nennt ihn nicht, und `tester` schon gar nicht (s. Kommentar an ACTIONS).
    'e2e.release',
    // #111: Massen-Übersetzung (admin hat sie über new Set(ACTIONS)).
    'catalog.translate_bulk',
    // #195: Weitergabe (admin hat sie über new Set(ACTIONS)).
    'run.handover',
    // #194: Behebung bestätigen (admin hat sie über new Set(ACTIONS)).
    'defect.confirm_fix',
  ]),
  verantwortlicher: new Set<Action>([
    'plan.manage',
    'catalog.edit',
    'run.execute',
    'defect.edit',
    'report.read',
    'signoff.confirm',
    'run.handover',
    'defect.confirm_fix',
  ]),
  consultant: new Set<Action>(['catalog.edit', 'defect.edit', 'report.read']),
  tester: new Set<Action>(['run.execute', 'run.handover', 'defect.confirm_fix', 'report.read']),
  betrachter: new Set<Action>(['report.read']),
};

/**
 * #195: Die Rollen, deren Basisrechte die Aktion tragen — ohne Scope-Verfeinerung und ohne
 * Projektbezug. Die Kollegen-Abfrage der Weitergabe braucht sie, um aus `user_roles` genau die
 * Nutzer zu finden, die einen Test überhaupt übernehmen dürften (`rolesWith('run.execute')`).
 * Aus `MATRIX` abgeleitet statt als zweite Liste gepflegt, damit eine neue Rolle nicht an einer
 * Stelle vergessen wird.
 */
export function rolesWith(action: Action): Role[] {
  return (Object.keys(MATRIX) as Role[]).filter((role) => MATRIX[role].has(action));
}

// Review-Fund M8: `can()` las die `project_id` einer Rollenzuweisung NIE. Eine ungescopte
// Rollenliste (die rohe Menge aus `GET /v1/auth/me` bzw. `user_roles`) war damit still eine
// kundenübergreifende Entscheidung — ohne Typfehler, ohne roten Test: Ein Tester in Projekt A, der
// in Projekt B Testmanager ist, hätte `plan.manage` bekommen, sobald irgendwer den Riegel
// (`scopeRolesToProject` in Middleware bzw. `useScopedUser()` im Client) umgeht oder vergisst.
//
// Deshalb ist eine Rollenliste mit mehr als EINER distinkten, nicht-null `project_id` hier ein
// Programmierfehler und keine Frage: sie wird fail-closed mit `false` beantwortet. Ein künftiger
// Bypass scheitert dadurch laut (403/verborgene UI) statt leise (zu viele Rechte).
//
// `null`/`undefined` als `project_id` zählt bewusst NICHT mit: Testfixtures und der
// Betreiber-Sonderfall arbeiten mit projektlosen Rollen, und für die ist nichts zu entscheiden.
// Vergleich case-insensitiv, wörtlich wie in `scopeRolesToProject` (project-scope.ts).
function spansMultipleProjects(user: AuthenticatedUser): boolean {
  const ids = new Set<string>();
  for (const a of user.roles) {
    if (a.project_id) ids.add(a.project_id.toLowerCase());
  }
  return ids.size > 1;
}

export function can(user: AuthenticatedUser, action: Action, ctx?: { node_id?: string }): boolean {
  if (spansMultipleProjects(user)) return false;
  return user.roles.some((a) => {
    if (!MATRIX[a.role].has(action)) return false;
    // Scope-gebundene Rolle MIT gesetztem Scope: erfordert passenden node_id-Kontext (#19, fail-closed).
    if (SCOPED_ROLES.includes(a.role) && a.scope_node_id !== null) {
      if (!ctx?.node_id) return false;
      return a.scope_node_id === ctx.node_id;
    }
    return true;
  });
}

/**
 * „Darf diese Rolle die Aktion in IRGENDEINEM ihrer Knoten?" — die Frage, die ein Navigationspunkt
 * stellt, und die Frage jedes Endpunkts, der GAR KEINEN Knoten kennt.
 *
 * Hintergrund (gemessen am 2026-09-09): `can()` ohne `ctx.node_id` beantwortet eine ANDERE Frage,
 * nämlich „darf sie es überall". Genau das fragte aber die App-Shell (`can(user, 'defect.edit')`),
 * und genau das fragen 99 der 101 `requirePermission(...)`-Stellen im Backend — sie übergeben
 * keinen Knoten, weil in ihrem Pfad/Query keiner steht. Ergebnis: ein Verantwortlicher mit
 * gesetztem `scope_node_id` verlor Fehlerboard, Zuordnung, Katalogpflege, Testplanpflege UND
 * „Meine Tests" — nicht nur in der Navigation, sondern mit 403 auch per Deeplink. Laut
 * docs/requirements.md §5 hat er all das mit „(Scope)".
 *
 * STUFE 1 (hier): Der Knoten-Scope entscheidet über die SICHT, nicht über das Recht. Wer die
 * Aktion in seinem Knoten darf, bekommt Navigationspunkt und Endpunkt — projektweit. Das ist eine
 * bewusste Lockerung gegenüber #19: Ein Consultant/Verantwortlicher des Teilprojekts „Vertrieb"
 * kann damit auch einen Fehler aus „Finanzen" bearbeiten. Der Tenant-Riegel bleibt unangetastet
 * (`spansMultipleProjects` + `scopeRolesToProject`): über das Projekt hinaus sieht niemand.
 *
 * STUFE 2 (offen, eigenes Issue): echte Knoten-Grenze im Datenzugriff — Defect-Listen nach Scope
 * filtern, Schreibzugriff gegen den Knoten des zugehörigen Testfalls prüfen
 * (Defect → Testfall → Prozess → Knoten), Katalog analog. Erst dann wird aus der Sicht-Vorgabe
 * eine Grenze. Bis dahin gilt: `canInAnyScope` = Sichtbarkeit, `can(..., { node_id })` = Grenze.
 *
 * Wo ein Knoten bekannt ist, bleibt `can(user, action, { node_id })` die richtige Frage — sie ist
 * unverändert fail-closed. Siehe signoff.ts (Bestätigen/Widerrufen am konkreten Knoten) und
 * reports.ts (`?node_id=`).
 */
export function canInAnyScope(user: AuthenticatedUser, action: Action): boolean {
  if (spansMultipleProjects(user)) return false;
  return user.roles.some((a) => MATRIX[a.role].has(action));
}

/**
 * Der wirksame Knoten-Zuschnitt für EINE Aktion — die Frage jedes Endpunkts, der die Daten
 * FILTERN statt sperren soll.
 *
 * `can()` und `canInAnyScope()` liefern ja/nein. Reporting und Sign-off brauchen mehr: Die
 * Berechtigungsmatrix (docs/requirements.md §5) spricht dem Verantwortlichen beides mit „(Scope)"
 * zu — also „auf den eigenen Struktur-Knoten begrenzt", nicht „gesperrt". Bis 2026-09-10 lasen
 * diese Endpunkte einen fehlenden `node_id`-Query-Parameter als „darf nicht" und antworteten einer
 * scope-gebundenen Rolle mit 403 auf JEDEN Aufruf — die Seite blieb leer, obwohl die Oberfläche
 * den Navigationspunkt zeigte. Der Knoten stand nämlich nie im Aufruf: kein einziger Client
 * übergab `node_id` (apps/web/src/lib/reports-api.ts, signoff-api.ts).
 *
 * Rückgabe:
 * - `null` — die Aktion fehlt der Rolle ganz (oder der Tenant-Riegel greift).
 * - `{ kind: 'projectwide' }` — mindestens eine Rolle trägt die Aktion ohne Knoten-Begrenzung.
 * - `{ kind: 'nodes', node_ids }` — die Aktion kommt AUSSCHLIESSLICH über knoten-gebundene
 *   Rollen; `node_ids` ist deren Vereinigung (dedupliziert, sortiert, nie leer).
 *
 * Eine zweite, ungescopte Rolle mit derselben Aktion gewinnt bewusst: Wer zusätzlich Betrachter
 * ist, darf nicht WENIGER sehen als ein reiner Betrachter.
 */
export type ReadScope = { kind: 'projectwide' } | { kind: 'nodes'; node_ids: string[] };

export function scopeFor(user: AuthenticatedUser, action: Action): ReadScope | null {
  if (spansMultipleProjects(user)) return null;
  const nodes = new Set<string>();
  let hasAction = false;
  for (const a of user.roles) {
    if (!MATRIX[a.role].has(action)) continue;
    hasAction = true;
    if (SCOPED_ROLES.includes(a.role) && a.scope_node_id !== null) nodes.add(a.scope_node_id);
    else return { kind: 'projectwide' };
  }
  if (!hasAction) return null;
  return { kind: 'nodes', node_ids: [...nodes].sort() };
}
